import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  message,
  Row,
  Col,
  Card,
  Statistic,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  PoweroffOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { inverter_api } from '../api/inverter-api';

const { Option } = Select;

export default function Inverters() {
  const [inverters, setInverters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInverter, setEditingInverter] = useState(null);
  const [filters, setFilters] = useState({
    status: undefined,
    search: '',
  });
  const [serialLookupLoading, setSerialLookupLoading] = useState(false);
  const [form] = Form.useForm();

  const loadInverters = async () => {
    try {
      setLoading(true);
      const response = await inverter_api.getAllInverters({
        limit: 1000,
        status: filters.status,
        search: filters.search?.trim() || undefined,
      });
      if (response.success) {
        setInverters(response.data || []);
      }
    } catch (error) {
      message.error('Không thể tải danh sách inverter: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  const handleSerialLookup = async () => {
    const serial = form.getFieldValue('serialNumber');
    if (!serial || `${serial}`.trim() === '') return;
    try {
      setSerialLookupLoading(true);
      const res = await inverter_api.getInverterBySerial(serial.trim());
      if (res.success && res.data) {
        form.setFieldsValue({
          name: res.data.name || '',
          model: res.data.model || '',
          ratedPower: res.data.systemInfo?.ratedPower ?? '',
        });
        message.success('Đã tự động điền thông tin từ serial');
      }
    } catch (error) {
      // Không báo lỗi lớn, chỉ log để tránh làm phiền người dùng
      console.warn('Serial lookup error:', error);
    } finally {
      setSerialLookupLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await inverter_api.getInverterStatistics();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      // Không chặn UI nếu lỗi thống kê
      console.error('Load inverter statistics error:', error);
    }
  };

  useEffect(() => {
    loadInverters();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.search]);

  const handleOpenCreate = () => {
    setEditingInverter(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingInverter(record);
    form.setFieldsValue({
      name: record.name,
      serialNumber: record.serialNumber,
      model: record.model || '',
      status: record.status || 'offline',
      ratedPower: record.systemInfo?.ratedPower ?? '',
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      setLoading(true);
      const response = await inverter_api.deleteInverter(record._id);
      if (response.success) {
        message.success('Xóa inverter thành công');
        await loadInverters();
        await loadStats();
      } else {
        message.error(response.message || 'Xóa inverter thất bại');
      }
    } catch (error) {
      message.error('Xóa inverter thất bại: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      name: values.name,
      serialNumber: values.serialNumber,
      model: values.model || '',
      status: values.status || 'offline',
      systemInfo: {
        ratedPower: values.ratedPower === '' ? undefined : Number(values.ratedPower),
      }
    };

    try {
      setLoading(true);
      let response;
      if (editingInverter) {
        response = await inverter_api.updateInverter(editingInverter._id, payload);
      } else {
        response = await inverter_api.createInverter(payload);
      }

      if (response.success) {
        message.success(editingInverter ? 'Cập nhật inverter thành công' : 'Tạo inverter thành công');
        setModalVisible(false);
        form.resetFields();
        await loadInverters();
        await loadStats();
      } else {
        message.error(response.message || 'Thao tác thất bại');
      }
    } catch (error) {
      message.error('Thao tác thất bại: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const map = {
          online: { color: 'green', text: 'Online' },
          offline: { color: 'default', text: 'Offline' },
          maintenance: { color: 'orange', text: 'Bảo trì' },
          error: { color: 'red', text: 'Lỗi' },
        };
        const info = map[status] || { color: 'default', text: status || 'N/A' };
        return <Tag color={info.color}>{info.text}</Tag>;
      }
    },
    {
      title: 'Kết nối',
      dataIndex: ['connection', 'connectionStatus'],
      key: 'connectionStatus',
      width: 110,
      render: (_, record) => {
        const status = record.connection?.connectionStatus;
        const map = {
          connected: { color: 'green', text: 'Connected' },
          disconnected: { color: 'default', text: 'Disconnected' },
        };
        const info = map[status] || { color: 'default', text: status || 'N/A' };
        return <Tag color={info.color}>{info.text}</Tag>;
      }
    },
    {
      title: 'Công suất (W)',
      key: 'powerOutput',
      align: 'right',
      width: 130,
      render: (_, record) => {
        const val = record.currentData?.powerOutput;
        return typeof val === 'number' ? val.toLocaleString() : '—';
      }
    },
    {
      title: 'Cập nhật',
      key: 'updatedAt',
      width: 160,
      render: (_, record) => {
        const time = record.currentData?.updatedAt || record.updatedAt;
        return time ? new Date(time).toLocaleString() : '—';
      }
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Sửa
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Xóa
          </Button>
        </Space>
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Quản lý Inverter</h1>
        <Space wrap>
          <Input
            allowClear
            placeholder="Tìm theo tên hoặc serial"
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onPressEnter={loadInverters}
          />
          <Select
            allowClear
            placeholder="Trạng thái"
            style={{ width: 160 }}
            value={filters.status}
            onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
            options={[
              { label: 'Online', value: 'online' },
              { label: 'Offline', value: 'offline' },
              { label: 'Bảo trì', value: 'maintenance' },
              { label: 'Lỗi', value: 'error' },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Thêm inverter
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng số inverter"
              value={stats?.total || 0}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Đang online"
              value={stats?.online || 0}
              prefix={<PoweroffOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Offline"
              value={stats?.offline || 0}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bảo trì / Lỗi"
              value={(stats?.maintenance || 0) + (stats?.error || 0)}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={inverters}
        rowKey="_id"
        loading={loading}
        size="middle"
        scroll={{ x: 1000 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} inverter`,
        }}
      />

      <Modal
        title={editingInverter ? 'Sửa inverter' : 'Thêm inverter mới'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tên inverter"
                name="name"
                rules={[{ required: true, message: 'Vui lòng nhập tên inverter' }]}
              >
                <Input placeholder="Nhập tên inverter" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Serial Number"
                name="serialNumber"
                rules={[{ required: true, message: 'Vui lòng nhập serial number' }]}
              >
                <Input
                  placeholder="Nhập serial number"
                  onBlur={handleSerialLookup}
                  suffix={serialLookupLoading ? <span style={{ fontSize: 12 }}>Đang tìm...</span> : null}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Model"
                name="model"
              >
                <Input placeholder="Nhập model" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Trạng thái"
                name="status"
                initialValue="offline"
              >
                <Select>
                  <Option value="online">Online</Option>
                  <Option value="offline">Offline</Option>
                  <Option value="maintenance">Bảo trì</Option>
                  <Option value="error">Lỗi</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Công suất định mức (W)"
                name="ratedPower"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === '' || value === undefined || value === null) return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num) || num < 0) return Promise.reject('Phải là số không âm');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={10} placeholder="Ví dụ: 5000" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingInverter ? 'Cập nhật' : 'Tạo mới'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

